// Cache testing utility to verify API caching is working

import { apiService } from '../services/api';

export const testCacheFunctionality = async () => {
    console.log('🧪 Testing Cache Functionality...');

    const testMemberId = 1; // Use a test member ID

    try {
        // First call - should make API request
        console.log('📡 First API call (should hit server)...');
        const startTime1 = performance.now();
        const result1 = await apiService.getMemberProfile(testMemberId);
        const time1 = performance.now() - startTime1;
        console.log(`⏱️ First call took: ${time1.toFixed(2)}ms`);

        // Check cache status
        const cacheStatus1 = apiService.getCacheStatus();
        console.log('📊 Cache after first call:', cacheStatus1);

        // Second call - should use cache
        console.log('📡 Second API call (should use cache)...');
        const startTime2 = performance.now();
        const result2 = await apiService.getMemberProfile(testMemberId);
        const time2 = performance.now() - startTime2;
        console.log(`⏱️ Second call took: ${time2.toFixed(2)}ms`);

        // Check cache status
        const cacheStatus2 = apiService.getCacheStatus();
        console.log('📊 Cache after second call:', cacheStatus2);

        // Verify results are the same
        const resultsMatch = JSON.stringify(result1) === JSON.stringify(result2);
        console.log('✅ Results match:', resultsMatch);

        // Performance improvement
        const improvement = ((time1 - time2) / time1) * 100;
        console.log(`🚀 Performance improvement: ${improvement.toFixed(1)}%`);

        if (time2 < time1) {
            console.log('🎉 Caching is working! Second call was faster.');
        } else {
            console.log('⚠️ Caching might not be working. Second call was not faster.');
        }

        return {
            firstCallTime: time1,
            secondCallTime: time2,
            improvement,
            resultsMatch,
            cacheWorking: time2 < time1
        };

    } catch (error) {
        console.error('❌ Cache test failed:', error);
        return null;
    }
};

export const testStudentSwitching = async () => {
    console.log('🔄 Testing Student Switching Cache...');

    const memberIds = [1, 2]; // Test with two different member IDs

    try {
        const results: Record<string | number, any> = {};

        for (const memberId of memberIds) {
            console.log(`\n👤 Testing Member ID: ${memberId}`);

            // First call for this member
            const startTime1 = performance.now();
            const result1 = await apiService.getMemberProfile(memberId);
            const time1 = performance.now() - startTime1;
            console.log(`⏱️ First call for member ${memberId}: ${time1.toFixed(2)}ms`);

            // Second call for this member (should use cache)
            const startTime2 = performance.now();
            const result2 = await apiService.getMemberProfile(memberId);
            const time2 = performance.now() - startTime2;
            console.log(`⏱️ Second call for member ${memberId}: ${time2.toFixed(2)}ms`);

            results[memberId] = {
                firstCallTime: time1,
                secondCallTime: time2,
                improvement: ((time1 - time2) / time1) * 100,
                cached: time2 < time1
            };
        }

        // Test switching back to first member
        console.log(`\n🔄 Switching back to Member ID: ${memberIds[0]}`);
        const startTime3 = performance.now();
        const result3 = await apiService.getMemberProfile(memberIds[0]);
        const time3 = performance.now() - startTime3;
        console.log(`⏱️ Call after switching back: ${time3.toFixed(2)}ms`);

        results['switchBack'] = {
            callTime: time3,
            cached: time3 < results[memberIds[0]].firstCallTime
        };

        console.log('\n📊 Student Switching Test Results:');
        console.log('=====================================');
        Object.entries(results).forEach(([key, value]: [string, any]) => {
            if (key === 'switchBack') {
                console.log(`Switch back to member ${memberIds[0]}: ${value.callTime.toFixed(2)}ms (${value.cached ? 'Cached' : 'Not Cached'})`);
            } else {
                console.log(`Member ${key}: ${value.improvement.toFixed(1)}% improvement (${value.cached ? 'Cached' : 'Not Cached'})`);
            }
        });

        return results;

    } catch (error) {
        console.error('❌ Student switching test failed:', error);
        return null;
    }
};

export const clearAllCache = () => {
    console.log('🧹 Clearing all cache...');
    const cacheStatus = apiService.getCacheStatus();
    console.log(`📊 Cache entries before clearing: ${cacheStatus.size}`);

    // Clear cache by calling clearMemberCache for known member IDs
    [1, 2, 3, 4, 5].forEach(memberId => {
        apiService.clearMemberCache(memberId);
    });

    const cacheStatusAfter = apiService.getCacheStatus();
    console.log(`📊 Cache entries after clearing: ${cacheStatusAfter.size}`);
    console.log('✅ Cache cleared successfully!');
};

// Export for global use
if (typeof window !== 'undefined') {
    (window as any).testCacheFunctionality = testCacheFunctionality;
    (window as any).testStudentSwitching = testStudentSwitching;
    (window as any).clearAllCache = clearAllCache;
} 